import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import ReportsPage from './pages/ReportsPage';
import PostReportsPage from './pages/PostReportsPage';
import TestReportsPage from './pages/TestReportsPage';
import SettingsPage from './pages/SettingsPage';
import DeletedUsersPage from './pages/DeletedUsersPage';
import DeveloperToolsPage from './pages/DeveloperToolsPage';
import FakeUserManagementPage from './pages/FakeUserManagementPage';
import SalesPage from './pages/SalesPage';
import PremiumSubscriptionsPage from './pages/PremiumSubscriptionsPage';
import PointApplicationsPage from './pages/PointApplicationsPage';
import AccountDeletionRequestsPage from './pages/AccountDeletionRequestsPage';
import MessagesPage from './pages/MessagesPage';
import ChatbotPage from './pages/ChatbotPage';
import LikeBotPage from './pages/LikeBotPage';
import AdminLayout from './components/AdminLayout';
import LoadingScreen from './components/LoadingScreen';
import './App.css';

// Material-UI tema
const theme = createTheme({
  palette: {
    primary: {
      main: '#F78D93', // Datezy pink color
    },
    secondary: {
      main: '#f50057',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Protected Route bileşeni
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, adminUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user || !adminUser) {
    return <Navigate to="/login" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
};

// Public Route bileşeni (sadece giriş yapmamış kullanıcılar için)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, adminUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user && adminUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } 
            />

            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/users" 
              element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/users/:userId" 
              element={
                <ProtectedRoute>
                  <UserDetailPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/point-applications" 
              element={
                <ProtectedRoute>
                  <PointApplicationsPage />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/reports" 
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/post-reports" 
              element={
                <ProtectedRoute>
                  <PostReportsPage />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/test-reports" 
              element={
                <ProtectedRoute>
                  <TestReportsPage />
                </ProtectedRoute>
              } 
            />

            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/deleted-users" element={
              <ProtectedRoute>
                <DeletedUsersPage />
              </ProtectedRoute>
            } />

            <Route path="/developer-tools" element={
              <ProtectedRoute>
                <DeveloperToolsPage />
              </ProtectedRoute>
            } />

            <Route path="/fake-user-management" element={
              <ProtectedRoute>
                <FakeUserManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/sales" element={
              <ProtectedRoute>
                <SalesPage />
              </ProtectedRoute>
            } />

            <Route path="/premium-subscriptions" element={
              <ProtectedRoute>
                <PremiumSubscriptionsPage />
              </ProtectedRoute>
            } />

            <Route path="/account-deletion-requests" element={
              <ProtectedRoute>
                <AccountDeletionRequestsPage />
              </ProtectedRoute>
            } />

            <Route path="/messages" element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            } />

            <Route path="/chatbot" element={
              <ProtectedRoute>
                <ChatbotPage />
              </ProtectedRoute>
            } />

            <Route path="/like-bot" element={
              <ProtectedRoute>
                <LikeBotPage />
              </ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* 404 fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
